import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'

interface LandmarkCategory {
    _id: string;
    name: string;
    icon: string;
}

export default function AdminLandmarkCategories() {
    const { token } = useAuth()
    const [categories, setCategories] = useState<LandmarkCategory[]>([])
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('')
    const [icon, setIcon] = useState('fa-location-dot')
    const [useImage, setUseImage] = useState(false)
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
        fetchCategories()
    }, [])

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API_BASE}/landmark-categories`)
            const data = await res.json()
            if (Array.isArray(data)) setCategories(data)
        } catch (err) {
            console.error(err)
            toast.error('Failed to sync landmark categories.')
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim() || !token) return
        setSubmitting(true)

        let finalIcon = icon

        try {
            // 1. Handle Image Upload if needed
            if (useImage && imageFile) {
                const fd = new FormData()
                fd.append('image', imageFile)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) finalIcon = uploadData.url
                else throw new Error('Icon upload failed')
            }

            // 2. Save/Update Category
            const url = editId ? `${API_BASE}/landmark-categories/${editId}` : `${API_BASE}/landmark-categories`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name: name.trim(), icon: finalIcon })
            })

            if (res.ok) {
                toast.success(editId ? 'Category synchronized successfully.' : 'New category registered.')
                setName('')
                setIcon('fa-location-dot')
                setUseImage(false)
                setImageFile(null)
                setImagePreview('')
                setEditId(null)
                setIsModalOpen(false)
                fetchCategories()
            } else {
                const errData = await res.json()
                toast.error(errData.message || 'Transmission failed.')
            }
        } catch (err) {
            toast.error('Connection error.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Erase this category from the matrix?')) return
        try {
            const res = await fetch(`${API_BASE}/landmark-categories/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setCategories(prev => prev.filter(c => c._id !== id))
                toast.success('Category purged.')
            }
        } catch (err) {
            console.error(err)
            toast.error('Purge failed.')
        }
    }

    const handleEdit = (c: LandmarkCategory) => {
        setName(c.name)
        setIcon(c.icon)
        setUseImage(!c.icon.startsWith('fa-'))
        setImagePreview(!c.icon.startsWith('fa-') ? c.icon : '')
        setImageFile(null)
        setEditId(c._id)
        setIsModalOpen(true)
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Landmark Categories</h1>
                    <p className="text-[14px] md:text-[15px] font-bold text-black leading-relaxed">Manage dynamic landmark classifiers for geographical nodes.</p>
                </div>
                <button 
                    onClick={() => {
                        setEditId(null)
                        setName('')
                        setIcon('fa-location-dot')
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-3 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] text-[14px] font-bold rounded-md transition-all shadow-md active:scale-95"
                >
                    <i className="fa-solid fa-plus-circle text-sm"></i>
                    Initialize Category
                </button>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white border-2 border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl shadow-blue-100">
                            <i className="fa-solid fa-map-pin text-xl"></i>
                        </div>
                        <div>
                            <h5 className="text-xl font-bold text-black leading-none">Category Registry</h5>
                            <p className="text-[12px] font-bold text-black/60 mt-1.5">{categories.length} segments identified</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32">
                        <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                        <p className="text-black font-bold text-[14px]">Accessing category records...</p>
                    </div>
                ) : categories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                        <div className="w-18 h-18 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border-2 border-gray-100 shadow-inner text-black/10">
                            <i className="fa-solid fa-map-pin text-2xl"></i>
                        </div>
                        <h5 className="text-xl font-bold text-black tracking-tight mb-2">Registry Empty</h5>
                        <p className="text-black/40 text-[13px] font-bold">Initialize your first geographical segment category.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto scrollbar-hide">
                        <table className="w-full text-left border-collapse">
                                <tr>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Icon</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Nomenclature</th>
                                    <th className="px-8 py-5 text-right text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Operations</th>
                                </tr>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {categories.map(c => (
                                    <tr key={c._id} className="hover:bg-blue-50/30 dark:hover:bg-slate-800/30 transition-all group">
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <div className="w-12 h-12 rounded-md bg-white border border-gray-100 flex items-center justify-center text-black shadow-sm group-hover:scale-110 transition-transform duration-300 overflow-hidden p-1">
                                                {!c.icon.startsWith('fa-') ? (
                                                    <img src={getImageUrl(c.icon)} alt={c.name} className="w-full h-full object-contain" />
                                                ) : (
                                                    <i className={`fa-solid ${c.icon} text-lg`}></i>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <div className="text-[15px] md:text-[16px] font-bold text-black leading-tight">{c.name}</div>
                                            <div className="text-[11px] font-bold text-black/40 mt-1 tracking-widest">LAND-${c._id.substring(0, 6)}</div>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <button
                                                    onClick={() => handleEdit(c)}
                                                    className="w-10 h-10 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center active:scale-95 shadow-sm"
                                                    title="Modify segment"
                                                >
                                                    <i className="fa-solid fa-pen-nib text-sm"></i>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(c._id)}
                                                    className="w-10 h-10 rounded-md bg-white border border-gray-100 text-black transition-all flex items-center justify-center active:scale-95 shadow-sm hover:border-rose-600 hover:text-rose-600"
                                                    title="Purge segment"
                                                >
                                                    <i className="fa-solid fa-trash-can text-sm"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-[#05144b]/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-md border border-gray-100 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-[#05144b] text-white">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white rounded-md flex items-center justify-center text-[#05144b] shadow-xl">
                                    <i className="fa-solid fa-map-location-dot text-lg"></i>
                                </div>
                                <div>
                                    <h5 className="text-xl font-bold text-white tracking-tight leading-none">
                                        {editId ? 'Modify Category' : 'New Category'}
                                    </h5>
                                    <p className="text-[10px] font-bold text-blue-100 uppercase tracking-widest mt-1.5">Protocol definition</p>
                                </div>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 text-white transition-all">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-black uppercase tracking-widest ml-1">Nomenclature</label>
                                    <input
                                        type="text"
                                        required
                                        className="block w-full px-6 py-4 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] rounded-md text-sm font-bold text-black transition-all outline-none shadow-sm"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="Education, Health, Parks..."
                                    />
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                        <button 
                                            type="button"
                                            onClick={() => setUseImage(false)}
                                            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded transition-all ${!useImage ? 'bg-[#05144b] text-white shadow-lg' : 'text-black/40 hover:text-black'}`}
                                        >
                                            Icon Class
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setUseImage(true)}
                                            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded transition-all ${useImage ? 'bg-[#05144b] text-white shadow-lg' : 'text-black/40 hover:text-black'}`}
                                        >
                                            Custom Image
                                        </button>
                                    </div>

                                    {!useImage ? (
                                        <div className="space-y-2 animate-in slide-in-from-left-2 duration-300">
                                            <label className="text-[12px] font-bold text-black uppercase tracking-widest ml-1">FontAwesome Icon</label>
                                            <div className="flex gap-4">
                                                <div className="w-14 h-14 bg-white border border-gray-100 flex items-center justify-center text-black rounded-md shadow-inner text-xl">
                                                    <i className={`fa-solid ${icon}`}></i>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="flex-1 px-4 py-4 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] rounded-md text-sm font-bold text-black transition-all outline-none shadow-sm"
                                                    value={icon}
                                                    onChange={e => setIcon(e.target.value)}
                                                    placeholder="fa-graduation-cap"
                                                />
                                            </div>
                                            <p className="text-[10px] text-black/40 mt-1 ml-1 font-bold uppercase tracking-widest">Example: fa-hospital, fa-cart-shopping</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 animate-in slide-in-from-right-2 duration-300">
                                            <label className="text-[12px] font-bold text-black uppercase tracking-widest ml-1">Iconographic Image</label>
                                            <div className="relative group">
                                                <div className={`relative h-28 rounded-md border-2 border-dashed transition-all flex flex-col items-center justify-center p-3 overflow-hidden ${imagePreview ? 'border-[#05144b] bg-gray-50' : 'border-gray-100 hover:border-[#05144b]/30 bg-white'}`}>
                                                    {imagePreview ? (
                                                        <div className="relative z-10 text-center">
                                                            <img src={getImageUrl(imagePreview)} alt="Preview" className="h-12 w-12 mx-auto rounded object-contain bg-white p-1 shadow-sm mb-1 border border-slate-50" />
                                                            <p className="text-[9px] font-bold text-black">REPLACE IMAGE</p>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center">
                                                            <i className="fa-solid fa-cloud-arrow-up text-black/30 mb-2"></i>
                                                            <p className="text-[10px] font-bold text-black/60">UPLOAD ICON</p>
                                                        </div>
                                                    )}
                                                    <input 
                                                        type="file" 
                                                        className="absolute inset-0 opacity-0 cursor-pointer z-20"
                                                        onChange={handleImageChange} 
                                                        required={!editId && useImage} 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button 
                                        type="button" 
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 py-4 text-[13px] font-bold text-black/30 hover:text-black transition-colors uppercase"
                                    >
                                        Discard
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-[2] py-4 bg-white border-2 border-[#05144b] text-[#05144b] text-[13px] font-bold uppercase tracking-widest rounded-md shadow-md active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                    >
                                        <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check-double'}`}></i>
                                        {submitting ? 'Syncing...' : editId ? 'Authorize Update' : 'Finalize registration'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}
